import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot,
  CalendarClock,
  ChevronDown,
  Columns3,
  Layers,
  Mail,
  Plus,
  RefreshCw,
  Search,
  SlidersHorizontal,
  Sparkles,
  Users,
  Linkedin,
  Filter,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import SubscriberModal from './SubscriberModal';
import CalendlyMeetingsModal from './CalendlyMeetingsModal';
import SendSubscriberInviteModal from './SendSubscriberInviteModal';
import SubscriberSelectionInsightsModal from './SubscriberSelectionInsightsModal';
import SubscriberBulkEditModal from './SubscriberBulkEditModal';
import SubscriberListFieldsPanel, {
  type SubscriberListFieldChip,
} from './SubscriberListFieldsPanel';
import SubscriberListFiltersPanel from './SubscriberListFiltersPanel';
import { tierLabel } from '../../lib/personalization';
import type { SubscriberRow } from './types';
import { channelLabel, channelsToKeyArray, sortChannels, type ChannelRow } from '../../lib/channels';
import {
  customFieldShowsImageInList,
  externalUrlHref,
  formatCustomFieldDisplayValue,
  isLikelyImageUrl,
  isLinkedInUrlCustomField,
  readCustomFieldUrl,
  sortDefinitions,
  type CustomDataMap,
  type SubscriberFieldDefinitionRow,
  type SubscriberFieldType,
  type SubscriberFieldUi,
} from '../../lib/subscriberCustomFields';
import {
  ensureSubscribersListLive,
  refreshSubscribersList,
  useSubscribersList,
} from '../../lib/subscribersListStore';
import { subscriberRoleLabel } from '../../lib/subscriberRoles';
import { subscriberEmailStatus } from '../../lib/subscriberEmailStatus';
import { formatSubscriberDate, subscriberDaysAgoLabel } from '../../lib/subscriberDates';
import { subscriberFunnelStatusLabel } from '../../lib/subscriberFunnelStatus';
import { subscriberSource, subscriberSourceLabel } from '../../lib/subscriberSource';
import {
  buildDefaultModes,
  columnModeFor,
  countDisplayedColumns,
  countEnforcedColumns,
  isColumnDisplayed,
  mergeFieldOrder,
  migrateStoredLayout,
  nextColumnMode,
  normalizeFieldsLayout,
  rowHasColumnValue,
  type ColumnMode,
  type FieldsLayout,
} from '../../lib/subscriberListColumns';
import {
  loadSubscriberListPrefs,
  persistSubscriberListPrefs,
} from '../../lib/subscriberListPreferences';
import { meetingToSubscriberDraft } from '../../lib/subscriberCalendly';
import type { CalendlyMeeting } from '../../lib/subscriberCalendly';
import { needsNewsletterInvite } from '../../lib/subscriberNewsletterInvite';
import { subscriberInviteGreetingName } from '../../lib/subscriberDisplay';
import { mockSemanticFilter } from '../../lib/subscriberAiMock';

const VISIBLE_FIELDS_STORAGE_KEY = 'lccto-subscribers-visible-fields';
const FIELDS_LAYOUT_STORAGE_KEY = 'lccto-subscribers-fields-layout-v2';

const BUILTIN_LIST_FIELDS = [
  { id: 'email', label: 'Email' },
  { id: 'name', label: 'Name' },
  { id: 'created_at', label: 'Created' },
  { id: 'email_status', label: 'Email status' },
  { id: 'user_id', label: 'Account' },
  { id: 'funnel_status', label: 'Funnel status' },
  { id: 'source', label: 'Source' },
  { id: 'last_contacted_at', label: 'Last connected' },
  { id: 'role', label: 'Role' },
  { id: 'channels', label: 'Channels' },
  { id: 'profile', label: 'Profile' },
  { id: 'actions', label: 'Action' },
] as const;

type BuiltinListFieldId = (typeof BUILTIN_LIST_FIELDS)[number]['id'];

/** Built-in columns shown on first visit and “Reset to default”. */
const DEFAULT_SHOWN_BUILTIN_IDS: readonly BuiltinListFieldId[] = [
  'email',
  'name',
  'created_at',
  'email_status',
  'funnel_status',
  'source',
  'last_contacted_at',
  'actions',
];

type SortableColumn =
  | 'email'
  | 'name'
  | 'email_status'
  | 'funnel_status'
  | 'source'
  | 'last_contacted_at'
  | 'created_at'
  | 'profile';

const SORTABLE_COLUMNS = new Set<SortableColumn>([
  'email',
  'name',
  'email_status',
  'funnel_status',
  'source',
  'last_contacted_at',
  'created_at',
  'profile',
]);

type ListFieldColumn =
  | { kind: 'builtin'; id: BuiltinListFieldId; label: string; key: string }
  | {
      kind: 'custom';
      id: string;
      label: string;
      key: string;
      fieldType: SubscriberFieldType;
      ui?: SubscriberFieldUi;
    };

function builtinFieldKey(id: BuiltinListFieldId): string {
  return `builtin:${id}`;
}

function customFieldKey(key: string): string {
  return `custom:${key}`;
}

const DEFAULT_VISIBLE_FIELD_KEYS = DEFAULT_SHOWN_BUILTIN_IDS.map((id) => builtinFieldKey(id));

function defaultFieldsLayout(customFields: SubscriberFieldDefinitionRow[]): FieldsLayout {
  const order = defaultFieldOrder(customFields);
  const modes = buildDefaultModes(
    order,
    DEFAULT_VISIBLE_FIELD_KEYS.filter((key) => order.includes(key)),
  );
  return { order, modes };
}

function defaultFieldOrder(customFields: SubscriberFieldDefinitionRow[]): string[] {
  const builtins = BUILTIN_LIST_FIELDS.map((field) => builtinFieldKey(field.id));
  const customs = sortDefinitions(customFields).map((field) => customFieldKey(field.key));
  const photoKey = customFieldKey('photo_url');
  if (!customs.includes(photoKey)) {
    return [...builtins, ...customs];
  }
  const nameIdx = builtins.indexOf(builtinFieldKey('name'));
  const insertAt = nameIdx >= 0 ? nameIdx + 1 : 1;
  const ordered = [...builtins];
  ordered.splice(insertAt, 0, photoKey);
  return [...ordered, ...customs.filter((key) => key !== photoKey)];
}

function persistFieldsLayout(layout: FieldsLayout) {
  try {
    localStorage.setItem(FIELDS_LAYOUT_STORAGE_KEY, JSON.stringify(layout));
  } catch {
    /* quota / private mode */
  }
}

function loadFieldsLayout(available: string[]): FieldsLayout {
  const defaultOrder = available.length > 0 ? available : DEFAULT_VISIBLE_FIELD_KEYS;
  const defaultShow = DEFAULT_VISIBLE_FIELD_KEYS.filter((key) => defaultOrder.includes(key));

  try {
    const raw = localStorage.getItem(FIELDS_LAYOUT_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { order?: unknown; visible?: unknown; modes?: unknown };
      const migrated = migrateStoredLayout(parsed, defaultOrder, defaultShow, {
        keepUnknownStored: true,
      });
      if (migrated) return migrated;
    }
  } catch {
    /* use defaults */
  }

  try {
    const legacy = localStorage.getItem(VISIBLE_FIELDS_STORAGE_KEY);
    if (legacy) {
      const parsed: unknown = JSON.parse(legacy);
      if (Array.isArray(parsed)) {
        const migrated = migrateStoredLayout(
          { order: parsed, visible: parsed },
          defaultOrder,
          defaultShow,
          { keepUnknownStored: true },
        );
        if (migrated) return migrated;
      }
    }
  } catch {
    /* use defaults */
  }

  return normalizeFieldsLayout(defaultOrder, buildDefaultModes(defaultOrder, defaultShow), defaultShow);
}

function columnHeaderLabel(column: ListFieldColumn, mode: ColumnMode): React.ReactNode {
  if (mode !== 'require_value') return column.label;
  return (
    <span className="inline-flex items-center gap-1">
      {column.label}
      <Filter className="w-3 h-3 text-amber-600 dark:text-amber-400" aria-hidden />
      <span className="sr-only"> — enforced (non-empty only)</span>
    </span>
  );
}

function customDataForRow(row: SubscriberRow): CustomDataMap {
  const cd = row.custom_data;
  if (cd && typeof cd === 'object' && !Array.isArray(cd)) {
    return cd as CustomDataMap;
  }
  return {};
}

function uniqCaseInsensitive(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const t = v.trim();
    if (!t) continue;
    const k = t.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out;
}

function bestEmail(row: SubscriberRow): string {
  const e = typeof row.email === 'string' ? row.email.trim() : '';
  if (e) return e;
  const alt = row.email_address ?? row.emailAddress;
  if (typeof alt === 'string') return alt.trim();
  return '';
}

function bestName(row: SubscriberRow): string {
  const n = typeof row.name === 'string' ? row.name.trim() : '';
  if (n) return n;
  const alt = row.full_name ?? row.fullName;
  if (typeof alt === 'string') return alt.trim();
  return '';
}

function linkedAccountId(row: SubscriberRow): string {
  return typeof row.user_id === 'string' ? row.user_id.trim() : '';
}

function parseSubscriberDate(value: unknown): Date | null {
  if (value == null) return null;
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function subscriberSubscribedDaysAgoLabel(d: Date): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const then = new Date(d);
  then.setHours(0, 0, 0, 0);
  const diffDays = Math.round((today.getTime() - then.getTime()) / (24 * 60 * 60 * 1000));
  if (diffDays < 0) return `in ${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'}`;
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return '1 day ago';
  return `${diffDays} days ago`;
}

function renderSubscribedCell(value: unknown): React.ReactNode {
  const d = parseSubscriberDate(value);
  if (!d) return '—';
  const dateLabel = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  return (
    <div>
      <div>{dateLabel}</div>
      <div className="text-[10px] leading-tight text-gray-400 dark:text-gray-500 mt-0.5">
        {subscriberSubscribedDaysAgoLabel(d)}
      </div>
    </div>
  );
}

function sortValueForColumn(row: SubscriberRow, column: SortableColumn): string | number | null {
  switch (column) {
    case 'email':
      return bestEmail(row).toLowerCase();
    case 'name':
      return bestName(row).toLowerCase();
    case 'email_status':
      return subscriberEmailStatus(row).toLowerCase();
    case 'funnel_status':
      return typeof row.funnel_status === 'string' ? row.funnel_status.toLowerCase() : '';
    case 'source':
      return subscriberSource(row).toLowerCase();
    case 'last_contacted_at': {
      const d = parseSubscriberDate(row.last_contacted_at);
      return d ? d.toISOString() : '';
    }
    case 'created_at': {
      const d = parseSubscriberDate(row.created_at);
      return d ? d.toISOString() : '';
    }
    case 'profile': {
      const score = row.profile_score;
      if (score == null || !Number.isFinite(Number(score))) return null;
      return Number(score);
    }
  }
}

function compareSortValues(a: string | number | null, b: string | number | null, direction: 'asc' | 'desc'): number {
  const emptyA = a == null || a === '';
  const emptyB = b == null || b === '';
  if (emptyA && emptyB) return 0;
  if (emptyA) return direction === 'asc' ? 1 : -1;
  if (emptyB) return direction === 'asc' ? -1 : 1;
  if (typeof a === 'number' && typeof b === 'number') {
    const cmp = a - b;
    return direction === 'asc' ? cmp : -cmp;
  }
  const cmp = String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
  return direction === 'asc' ? cmp : -cmp;
}

/** List is always ordered by `created_at` descending (newest first). */
function compareCreatedAtNewestFirst(a: SubscriberRow, b: SubscriberRow): number {
  return compareSortValues(
    sortValueForColumn(a, 'created_at'),
    sortValueForColumn(b, 'created_at'),
    'desc',
  );
}

function SubscriberListPhotoCell({ url }: { url: string }) {
  const [broken, setBroken] = useState(false);
  const trimmed = url.trim();
  if (!trimmed || broken) return <>—</>;
  return (
    <img
      src={trimmed}
      alt=""
      title={trimmed}
      loading="lazy"
      className="h-10 w-10 rounded-full object-cover border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-700"
      onError={() => setBroken(true)}
    />
  );
}

function listColumnShowsImage(column: ListFieldColumn): boolean {
  if (column.kind !== 'custom') return false;
  return customFieldShowsImageInList({
    key: column.id,
    field_type: column.fieldType,
    ui: column.ui,
  });
}

function renderListFieldCell(
  row: SubscriberRow,
  column: ListFieldColumn,
  channelCatalog: ChannelRow[],
): React.ReactNode {
  if (column.kind === 'builtin') {
    switch (column.id) {
      case 'email': {
        const email = bestEmail(row);
        return (
          <span className="truncate block" title={email}>
            {email || '—'}
          </span>
        );
      }
      case 'name':
        return bestName(row) || '—';
      case 'role': {
        const label = subscriberRoleLabel(row.role);
        return label || '—';
      }
      case 'channels': {
        const streams = uniqCaseInsensitive(channelsToKeyArray(row.channels));
        if (streams.length === 0) {
          return <span className="text-sm text-gray-400 dark:text-gray-500">—</span>;
        }
        return (
          <div className="flex flex-wrap gap-1.5 max-w-[26rem]">
            {streams.slice(0, 4).map((s, i) => (
              <span
                key={`${s}-${i}`}
                className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200"
                title={s}
              >
                {channelLabel(s, channelCatalog)}
              </span>
            ))}
            {streams.length > 4 ? (
              <span className="text-xs text-gray-400 dark:text-gray-500">+{streams.length - 4}</span>
            ) : null}
          </div>
        );
      }
      case 'profile':
        if (row.profile_score != null) {
          return (
            <span
              title={
                row.profile_score_breakdown ? JSON.stringify(row.profile_score_breakdown) : undefined
              }
            >
              {row.profile_score}
              {row.profile_score_tier ? ` · ${tierLabel(row.profile_score_tier)}` : ''}
            </span>
          );
        }
        return '—';
      case 'email_status': {
        const emailStatus = subscriberEmailStatus(row);
        return emailStatus || '—';
      }
      case 'user_id': {
        const uid = linkedAccountId(row);
        return uid ? (
          <span
            className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200"
            title={uid}
          >
            Linked
          </span>
        ) : (
          <span className="text-sm text-gray-400 dark:text-gray-500">Not provisioned</span>
        );
      }
      case 'funnel_status': {
        const label = subscriberFunnelStatusLabel(row.funnel_status);
        return label ? (
          <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-sky-50 text-sky-800 dark:bg-sky-900/30 dark:text-sky-200">
            {label}
          </span>
        ) : (
          '—'
        );
      }
      case 'source': {
        const source = subscriberSource(row);
        if (!source) return '—';
        const label = subscriberSourceLabel(source);
        return (
          <span className="truncate block text-sm" title={source}>
            {label || source}
          </span>
        );
      }
      case 'last_contacted_at': {
        const d = parseSubscriberDate(row.last_contacted_at);
        if (!d) return '—';
        return (
          <div>
            <div>{formatSubscriberDate(d)}</div>
            <div className="text-[10px] leading-tight text-gray-400 dark:text-gray-500 mt-0.5">
              {subscriberDaysAgoLabel(d)}
            </div>
          </div>
        );
      }
      case 'created_at':
        return renderSubscribedCell(row.created_at);
      default:
        return '—';
    }
  }

  const value = customDataForRow(row)[column.id];
  const url = readCustomFieldUrl(value);
  if (listColumnShowsImage(column) && url) {
    return <SubscriberListPhotoCell url={url} />;
  }

  const text = formatCustomFieldDisplayValue(value, column.fieldType);
  if (!text) return '—';

  if (column.fieldType === 'url' && isLikelyImageUrl(text)) {
    return <SubscriberListPhotoCell url={text} />;
  }

  if (isLinkedInUrlCustomField(column)) {
    return (
      <a
        href={externalUrlHref(text)}
        target="_blank"
        rel="noopener noreferrer"
        title={text}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        className="inline-flex rounded-md p-1 text-[#0A66C2] hover:bg-blue-50 dark:hover:bg-blue-950/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        <Linkedin className="w-4 h-4" aria-hidden />
        <span className="sr-only">Open LinkedIn profile</span>
      </a>
    );
  }

  return text;
}

function isRowInteractiveTarget(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest('a, button, input, textarea, select, label'));
}

let lastSubscriberTabAt = 0;

function openSubscriberInNewTab(id: string) {
  const now = Date.now();
  if (now - lastSubscriberTabAt < 500) return;
  lastSubscriberTabAt = now;
  window.open(`/subscribers/${encodeURIComponent(id)}`, '_blank', 'noopener,noreferrer');
}

export default function SubscribersView() {
  const navigate = useNavigate();
  const list = useSubscribersList();
  const rows = list.rows;
  const channelCatalog = list.channels;
  const fieldDefinitions = list.fieldDefinitions;
  const fieldDefinitionsReady = list.hydrated;
  const loadError = list.hydrated ? null : list.error;
  const loading = !list.hydrated && !list.error;
  const hydratedOnMount = useRef(list.hydrated);
  const initialPrefs = useMemo(() => loadSubscriberListPrefs(), []);
  const [searchQuery, setSearchQuery] = useState(initialPrefs.searchQuery);
  const [selectedChannelFilters, setSelectedChannelFilters] = useState<string[]>(
    initialPrefs.selectedChannelFilters,
  );
  const [emailStatusFilter, setEmailStatusFilter] = useState(initialPrefs.emailStatusFilter);
  const [funnelStatusFilter, setFunnelStatusFilter] = useState(initialPrefs.funnelStatusFilter);
  const [sourceFilter, setSourceFilter] = useState(initialPrefs.sourceFilter);
  const [filtersPanelCollapsed, setFiltersPanelCollapsed] = useState(
    initialPrefs.filtersPanelCollapsed,
  );
  const [fieldsPanelCollapsed, setFieldsPanelCollapsed] = useState(initialPrefs.fieldsPanelCollapsed);
  const [fieldsLayout, setFieldsLayout] = useState<FieldsLayout>(() =>
    loadFieldsLayout(defaultFieldOrder([])),
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [current, setCurrent] = useState<SubscriberRow | null>(null);
  const [subscriberDraft, setSubscriberDraft] = useState<Partial<SubscriberRow> | null>(null);
  const [calendlyModalOpen, setCalendlyModalOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteTarget, setInviteTarget] = useState<{
    email: string;
    name: string | null;
    subscriberId: string | null;
  } | null>(null);
  const [insightsModalOpen, setInsightsModalOpen] = useState(false);
  const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [needsInviteFilter, setNeedsInviteFilter] = useState(false);
  const [accountFilter, setAccountFilter] = useState<'' | 'linked' | 'unlinked'>('');
  const aiDropdownRef = useRef<HTMLDivElement>(null);
  const [aiDropdownOpen, setAiDropdownOpen] = useState(false);
  const [semanticQuery, setSemanticQuery] = useState('');
  const [semanticLoading, setSemanticLoading] = useState(false);
  const [aiFilteredIds, setAiFilteredIds] = useState<string[] | null>(null);
  const [aiSimilarity, setAiSimilarity] = useState<Record<string, number>>({});

  const fetchSubscribers = () => refreshSubscribersList();

  useEffect(() => {
    ensureSubscribersListLive();
    void refreshSubscribersList({ silent: hydratedOnMount.current });
  }, []);

  useEffect(() => {
    persistSubscriberListPrefs({
      searchQuery,
      selectedChannelFilters,
      emailStatusFilter,
      funnelStatusFilter,
      sourceFilter,
      filtersPanelCollapsed,
      fieldsPanelCollapsed,
      sortConfig: null,
    });
  }, [
    searchQuery,
    selectedChannelFilters,
    emailStatusFilter,
    funnelStatusFilter,
    sourceFilter,
    filtersPanelCollapsed,
    fieldsPanelCollapsed,
  ]);

  const sortedCustomFields = useMemo(
    () => sortDefinitions(fieldDefinitions.filter((def) => def.active)),
    [fieldDefinitions],
  );

  useEffect(() => {
    if (!fieldDefinitionsReady) return;
    const available = defaultFieldOrder(sortedCustomFields);
    const defaultShow = DEFAULT_VISIBLE_FIELD_KEYS.filter((key) => available.includes(key));
    setFieldsLayout((prev) => {
      const order = mergeFieldOrder(prev.order, available);
      const modes = { ...prev.modes };
      for (const key of order) {
        if (!(key in modes)) modes[key] = 'hidden';
      }
      const orderSet = new Set(order);
      const prunedModes = Object.fromEntries(
        Object.entries(modes).filter(([key]) => orderSet.has(key)),
      ) as Record<string, ColumnMode>;
      const next = normalizeFieldsLayout(order, prunedModes, defaultShow);
      if (next.order.join('|') === prev.order.join('|') && JSON.stringify(next.modes) === JSON.stringify(prev.modes)) {
        return prev;
      }
      persistFieldsLayout(next);
      return next;
    });
  }, [sortedCustomFields, fieldDefinitionsReady]);

  const fieldChips = useMemo(() => {
    return fieldsLayout.order
      .map((key) => {
        const mode = columnModeFor(fieldsLayout, key);
        if (key.startsWith('builtin:')) {
          const id = key.slice('builtin:'.length) as BuiltinListFieldId;
          const meta = BUILTIN_LIST_FIELDS.find((field) => field.id === id);
          if (meta) return { key, label: meta.label, mode };
        }
        if (key.startsWith('custom:')) {
          const id = key.slice('custom:'.length);
          const def = sortedCustomFields.find((field) => field.key === id);
          if (def) return { key, label: def.label, title: def.ui.help_text ?? def.key, mode };
        }
        return null;
      })
      .filter((chip): chip is SubscriberListFieldChip => chip != null);
  }, [fieldsLayout, sortedCustomFields]);

  const visibleColumns = useMemo(() => {
    const columns: ListFieldColumn[] = [];
    for (const key of fieldsLayout.order) {
      if (!isColumnDisplayed(columnModeFor(fieldsLayout, key))) continue;
      if (key.startsWith('builtin:')) {
        const id = key.slice('builtin:'.length) as BuiltinListFieldId;
        const meta = BUILTIN_LIST_FIELDS.find((field) => field.id === id);
        if (meta) {
          columns.push({ kind: 'builtin', id, label: meta.label, key });
        }
        continue;
      }
      if (key.startsWith('custom:')) {
        const id = key.slice('custom:'.length);
        const def = sortedCustomFields.find((field) => field.key === id);
        if (def) {
          columns.push({
            kind: 'custom',
            id,
            label: def.label,
            key,
            fieldType: def.field_type,
            ui: def.ui,
          });
        }
      }
    }
    return columns;
  }, [fieldsLayout, sortedCustomFields]);

  const enforcedColumns = useMemo(
    () =>
      visibleColumns.filter(
        (column) => columnModeFor(fieldsLayout, column.key) === 'require_value',
      ),
    [visibleColumns, fieldsLayout],
  );

  const cycleColumnMode = (key: string) => {
    setFieldsLayout((prev) => {
      const current = columnModeFor(prev, key);
      const nextMode = nextColumnMode(current);
      if (isColumnDisplayed(current) && !isColumnDisplayed(nextMode) && countDisplayedColumns(prev) <= 1) {
        toast.error('At least one column must stay visible');
        return prev;
      }
      const next = { ...prev, modes: { ...prev.modes, [key]: nextMode } };
      persistFieldsLayout(next);
      return next;
    });
  };

  const reorderFields = (order: string[]) => {
    setFieldsLayout((prev) => {
      const next = { ...prev, order };
      persistFieldsLayout(next);
      return next;
    });
  };

  const resetVisibleFields = () => {
    const next = defaultFieldsLayout(sortedCustomFields);
    setFieldsLayout(next);
    persistFieldsLayout(next);
  };

  const displayedColumnCount = useMemo(() => countDisplayedColumns(fieldsLayout), [fieldsLayout]);
  const enforcedColumnCount = useMemo(() => countEnforcedColumns(fieldsLayout), [fieldsLayout]);

  const availableEmailStatuses = useMemo(() => {
    const statuses = new Set<string>();
    for (const row of rows) {
      const emailStatus = subscriberEmailStatus(row);
      if (emailStatus) statuses.add(emailStatus);
    }
    return [...statuses].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [rows]);

  const availableFunnelStatuses = useMemo(() => {
    const statuses = new Set<string>();
    for (const row of rows) {
      const funnelStatus = typeof row.funnel_status === 'string' ? row.funnel_status.trim() : '';
      if (funnelStatus) statuses.add(funnelStatus);
    }
    return [...statuses].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [rows]);

  const availableSources = useMemo(() => {
    const sources = new Set<string>();
    for (const row of rows) {
      const source = subscriberSource(row);
      if (source) sources.add(source);
    }
    return [...sources].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [rows]);

  const clearFilters = () => {
    setSelectedChannelFilters([]);
    setEmailStatusFilter('');
    setFunnelStatusFilter('');
    setSourceFilter('');
    setNeedsInviteFilter(false);
    setAccountFilter('');
  };

  const existingSubscriberEmails = useMemo(
    () => new Set(rows.map((r) => bestEmail(r).trim().toLowerCase()).filter(Boolean)),
    [rows],
  );

  const clearAiFilter = useCallback(() => {
    setAiFilteredIds(null);
    setAiSimilarity({});
    setSemanticQuery('');
  }, []);

  useEffect(() => {
    if (!aiDropdownOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const el = aiDropdownRef.current;
      if (el && !el.contains(e.target as Node)) setAiDropdownOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [aiDropdownOpen]);

  const runSemanticSearch = () => {
    const q = semanticQuery.trim();
    if (!q) {
      toast.error('Describe who you are looking for.');
      return;
    }
    setSemanticLoading(true);
    try {
      const matches = mockSemanticFilter(filtered, q, 50);
      const ids = matches.map((m) => String(m.row.id ?? bestEmail(m.row)));
      const sim: Record<string, number> = {};
      for (const m of matches) {
        const id = String(m.row.id ?? bestEmail(m.row));
        sim[id] = m.similarity;
      }
      setAiFilteredIds(ids);
      setAiSimilarity(sim);
      setAiDropdownOpen(false);
      toast.success(
        matches.length === 0
          ? 'No mock matches — try different keywords'
          : `Showing ${matches.length} mock match${matches.length === 1 ? '' : 'es'}`,
      );
    } finally {
      setSemanticLoading(false);
    }
  };

  const openInviteForRow = (row: SubscriberRow, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const email = bestEmail(row);
    if (!email) return;
    setInviteTarget({
      email,
      name: subscriberInviteGreetingName(row) || null,
      subscriberId: row.id != null ? String(row.id) : null,
    });
    setInviteModalOpen(true);
  };

  const handleCalendlyImport = (meeting: CalendlyMeeting) => {
    setCalendlyModalOpen(false);
    setCurrent(null);
    setSubscriberDraft(meetingToSubscriberDraft(meeting));
    setModalOpen(true);
  };

  const rowId = (r: SubscriberRow, idx: number) =>
    r.id != null ? String(r.id) : bestEmail(r) || `row-${idx}`;

  const fieldsDifferFromDefault = useMemo(() => {
    const defaultLayout = defaultFieldsLayout(sortedCustomFields);
    if (fieldsLayout.order.join('|') !== defaultLayout.order.join('|')) return true;
    for (const key of defaultLayout.order) {
      if (columnModeFor(fieldsLayout, key) !== columnModeFor(defaultLayout, key)) return true;
    }
    return false;
  }, [fieldsLayout, sortedCustomFields]);

  const filterableChannels = useMemo(() => {
    const discovered = new Set(rows.flatMap((r) => channelsToKeyArray(r.channels)));
    const activeCatalog = sortChannels(channelCatalog.filter((c) => c.active));
    const catalogSlugs = activeCatalog.map((c) => c.slug);
    const catalogSet = new Set(catalogSlugs);
    const orphans = [...discovered]
      .filter((s) => !catalogSet.has(s))
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    return { activeCatalog, slugs: [...catalogSlugs, ...orphans] };
  }, [rows, channelCatalog]);

  const toggleChannelFilter = (slug: string) => {
    setSelectedChannelFilters((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  };

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const requiredChannels = selectedChannelFilters;
    return rows.filter((r) => {
      const email = bestEmail(r).toLowerCase();
      const name = bestName(r).toLowerCase();
      const emailStatus = subscriberEmailStatus(r).toLowerCase();
      const funnelStatusRaw = typeof r.funnel_status === 'string' ? r.funnel_status.toLowerCase() : '';
      const funnelStatusLabel = subscriberFunnelStatusLabel(r.funnel_status).toLowerCase();
      const source = subscriberSource(r).toLowerCase();
      const sourceLabel = subscriberSourceLabel(subscriberSource(r)).toLowerCase();
      const lastContacted = parseSubscriberDate(r.last_contacted_at);
      const lastContactedText = lastContacted
        ? `${formatSubscriberDate(lastContacted)} ${subscriberDaysAgoLabel(lastContacted)}`.toLowerCase()
        : '';
      const streams = channelsToKeyArray(r.channels);
      if (requiredChannels.length > 0) {
        const streamSet = new Set(streams);
        const hasAll = requiredChannels.every((slug) => streamSet.has(slug));
        if (!hasAll) return false;
      }
      if (emailStatusFilter) {
        const rowEmailStatus = subscriberEmailStatus(r).toLowerCase();
        if (rowEmailStatus !== emailStatusFilter.toLowerCase()) return false;
      }
      if (funnelStatusFilter) {
        const rowFunnel = typeof r.funnel_status === 'string' ? r.funnel_status.trim().toLowerCase() : '';
        if (rowFunnel !== funnelStatusFilter.toLowerCase()) return false;
      }
      if (sourceFilter) {
        if (subscriberSource(r).toLowerCase() !== sourceFilter.toLowerCase()) return false;
      }
      if (needsInviteFilter && !needsNewsletterInvite(r)) return false;
      if (accountFilter) {
        const linked = linkedAccountId(r).length > 0;
        if (accountFilter === 'linked' && !linked) return false;
        if (accountFilter === 'unlinked' && linked) return false;
      }
      for (const column of enforcedColumns) {
        if (!rowHasColumnValue(r, column)) return false;
      }
      if (!q) return true;
      return (
        email.includes(q) ||
        name.includes(q) ||
        emailStatus.includes(q) ||
        funnelStatusRaw.includes(q) ||
        funnelStatusLabel.includes(q) ||
        source.includes(q) ||
        sourceLabel.includes(q) ||
        lastContactedText.includes(q) ||
        streams.some((s) => s.toLowerCase().includes(q))
      );
    });
  }, [rows, searchQuery, selectedChannelFilters, emailStatusFilter, funnelStatusFilter, sourceFilter, needsInviteFilter, accountFilter, enforcedColumns]);

  const sortedFiltered = useMemo(() => {
    let list = filtered;
    if (aiFilteredIds) {
      const order = new Map(aiFilteredIds.map((id, i) => [id, i]));
      list = filtered.filter((r, idx) => order.has(rowId(r, idx)));
    }
    return [...list].sort(compareCreatedAtNewestFirst);
  }, [filtered, aiFilteredIds]);

  const selectedRows = useMemo(
    () => sortedFiltered.filter((r, idx) => selectedIds.has(rowId(r, idx))),
    [sortedFiltered, selectedIds],
  );

  const allVisibleSelected =
    sortedFiltered.length > 0 &&
    sortedFiltered.every((r, idx) => selectedIds.has(rowId(r, idx)));

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        sortedFiltered.forEach((r, idx) => next.delete(rowId(r, idx)));
      } else {
        sortedFiltered.forEach((r, idx) => next.add(rowId(r, idx)));
      }
      return next;
    });
  };

  const toggleRowSelected = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
            <Users className="w-8 h-8 mr-3 text-emerald-500" />
            Connections
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">Manage subscribers and email by channel.</p>
          {!loading && !loadError ? (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              <span className="font-medium tabular-nums text-gray-700 dark:text-gray-300">
                {rows.length.toLocaleString()}
              </span>{' '}
              total
              <span className="mx-2 text-gray-300 dark:text-gray-600">·</span>
              <span className="font-medium tabular-nums text-gray-700 dark:text-gray-300">
                {filtered.length.toLocaleString()}
              </span>{' '}
              shown
            </p>
          ) : null}
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => setFiltersPanelCollapsed((prev) => !prev)}
            aria-pressed={!filtersPanelCollapsed}
            aria-label="Filters"
            className={`inline-flex items-center justify-center p-2 rounded-lg border transition-colors ${
              !filtersPanelCollapsed
                ? 'border-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <SlidersHorizontal className="w-4 h-4" />
          </button>

          <button
            type="button"
            onClick={() => setFieldsPanelCollapsed((prev) => !prev)}
            aria-pressed={!fieldsPanelCollapsed}
            aria-label="Columns"
            className={`inline-flex items-center justify-center p-2 rounded-lg border transition-colors ${
              !fieldsPanelCollapsed
                ? 'border-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <Columns3 className="w-4 h-4" />
          </button>

          <div className="relative" ref={aiDropdownRef}>
            <button
              type="button"
              onClick={() => setAiDropdownOpen((o) => !o)}
              aria-pressed={Boolean(aiFilteredIds) || aiDropdownOpen}
              aria-label="AI filter"
              className={`inline-flex items-center justify-center p-2 rounded-lg border transition-colors ${
                aiFilteredIds
                  ? 'border-violet-500 bg-violet-50 text-violet-800 dark:bg-violet-900/30 dark:text-violet-200'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Bot className="w-4 h-4" />
            </button>
            {aiDropdownOpen ? (
              <div className="absolute right-0 top-full mt-2 z-20 w-72 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Mock keyword search — embeddings later</p>
                <input
                  type="text"
                  value={semanticQuery}
                  onChange={(e) => setSemanticQuery(e.target.value)}
                  placeholder="e.g. founder, SaaS, pickle"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    onClick={runSemanticSearch}
                    disabled={semanticLoading}
                    className="flex-1 px-3 py-1.5 text-sm font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
                  >
                    {semanticLoading ? 'Searching…' : 'Search'}
                  </button>
                  {aiFilteredIds ? (
                    <button
                      type="button"
                      onClick={clearAiFilter}
                      className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600"
                    >
                      Clear
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>

          <button
            type="button"
            onClick={() => setCalendlyModalOpen(true)}
            aria-label="Import from Calendly"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm"
          >
            <CalendarClock className="w-4 h-4" />
            Calendly
          </button>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search…"
              className="pl-10 pr-9 py-2.5 w-64 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery ? (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-md text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="w-4 h-4" />
              </button>
            ) : null}
          </div>

          <button
            type="button"
            onClick={() => void fetchSubscribers()}
            disabled={loading}
            aria-label="Refresh"
            className="inline-flex items-center justify-center p-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading || list.refreshing ? 'animate-spin' : ''}`} />
          </button>

          <button
            type="button"
            onClick={() => {
              setCurrent(null);
              setModalOpen(true);
            }}
            aria-label="Add subscriber"
            className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!filtersPanelCollapsed ? (
        <SubscriberListFiltersPanel
          collapsed={false}
          onCollapsedChange={() => setFiltersPanelCollapsed(true)}
          channelSlugs={filterableChannels.slugs}
          channelCatalog={channelCatalog}
          selectedChannelFilters={selectedChannelFilters}
          onToggleChannel={toggleChannelFilter}
          onClearFilters={clearFilters}
          emailStatusFilter={emailStatusFilter}
          onEmailStatusFilterChange={setEmailStatusFilter}
          availableEmailStatuses={availableEmailStatuses}
          funnelStatusFilter={funnelStatusFilter}
          onFunnelStatusFilterChange={setFunnelStatusFilter}
          availableFunnelStatuses={availableFunnelStatuses}
          sourceFilter={sourceFilter}
          onSourceFilterChange={setSourceFilter}
          availableSources={availableSources}
          needsInviteFilter={needsInviteFilter}
          onNeedsInviteFilterChange={setNeedsInviteFilter}
          accountFilter={accountFilter}
          onAccountFilterChange={setAccountFilter}
        />
      ) : null}

      {!fieldsPanelCollapsed ? (
        <SubscriberListFieldsPanel
          fields={fieldChips}
          displayedCount={displayedColumnCount}
          enforcedCount={enforcedColumnCount}
          collapsed={false}
          onCollapsedChange={() => setFieldsPanelCollapsed(true)}
          onReorder={reorderFields}
          onCycleMode={cycleColumnMode}
          onReset={resetVisibleFields}
          showReset={fieldsDifferFromDefault}
        />
      ) : null}

      {aiFilteredIds ? (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-violet-200 bg-violet-50/80 px-4 py-2 text-sm dark:border-violet-800 dark:bg-violet-950/30">
          <span className="text-violet-900 dark:text-violet-200">
            Mock AI filter active — {sortedFiltered.length} shown
          </span>
          <button
            type="button"
            onClick={clearAiFilter}
            className="inline-flex items-center gap-1 text-violet-800 dark:text-violet-300 hover:underline"
          >
            <X className="w-4 h-4" />
            Clear
          </button>
        </div>
      ) : null}

      {selectedIds.size > 0 ? (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50/80 px-4 py-3 dark:border-emerald-800 dark:bg-emerald-950/30">
          <span className="text-sm font-medium text-emerald-900 dark:text-emerald-200">
            {selectedIds.size} selected
          </span>
          <button
            type="button"
            onClick={() => setBulkEditModalOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
          >
            <Layers className="w-4 h-4" />
            Bulk edit
          </button>
          <button
            type="button"
            onClick={() => setInsightsModalOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-700"
          >
            <Sparkles className="w-4 h-4" />
            Find commonalities
          </button>
          {selectedRows.length === 1 && needsNewsletterInvite(selectedRows[0]!) ? (
            <button
              type="button"
              onClick={() => openInviteForRow(selectedRows[0]!)}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-emerald-600 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-100 dark:hover:bg-emerald-900/40"
            >
              <Mail className="w-4 h-4" />
              Draft invite
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="text-sm text-gray-600 dark:text-gray-400 hover:underline ml-auto"
          >
            Clear selection
          </button>
        </div>
      ) : null}

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="p-12 flex justify-center">
            <div className="animate-spin h-8 w-8 border-4 border-emerald-500 rounded-full border-t-transparent" />
          </div>
        ) : loadError ? (
          <div className="p-8">
            <p className="text-sm font-medium text-red-700 dark:text-red-300">Could not load subscribers.</p>
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300 break-words">{loadError}</p>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              Common causes: RLS policy blocking reads on <code className="font-mono">subscribers</code>, or missing
              <code className="font-mono"> created_at</code> column (ordering). I retry without ordering, but RLS still
              needs a SELECT policy.
            </p>
            <button
              type="button"
              onClick={() => void fetchSubscribers()}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
            >
              <RefreshCw className="w-4 h-4" />
              Try again
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-3 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      aria-label="Select all visible subscribers"
                      className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                  </th>
                  {visibleColumns.map((column) => {
                    const columnMode = columnModeFor(fieldsLayout, column.key);
                    const header = columnHeaderLabel(column, columnMode);
                    const isCreatedColumn = column.kind === 'builtin' && column.id === 'created_at';
                    return (
                      <th
                        key={column.key}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                        aria-sort={isCreatedColumn ? 'descending' : undefined}
                      >
                        {isCreatedColumn ? (
                          <span
                            className="inline-flex items-center gap-1"
                            title="Always sorted newest first"
                          >
                            {header}
                            <ChevronDown
                              className="w-4 h-4 shrink-0 text-emerald-600 dark:text-emerald-400"
                              aria-hidden
                            />
                          </span>
                        ) : (
                          header
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {sortedFiltered.map((r, idx) => {
                  const email = bestEmail(r);
                  const id = rowId(r, idx);
                  const subscriberId = r.id != null ? String(r.id) : null;
                  const openRow = () => {
                    if (subscriberId) {
                      navigate(`/subscribers/${encodeURIComponent(subscriberId)}`);
                    } else {
                      setCurrent(r);
                      setSubscriberDraft(null);
                      setModalOpen(true);
                    }
                  };
                  return (
                    <tr
                      key={id}
                      role="button"
                      tabIndex={0}
                      onMouseDown={(e) => {
                        if (e.button === 1 && subscriberId && !isRowInteractiveTarget(e.target)) {
                          e.preventDefault();
                        }
                      }}
                      onClick={(e) => {
                        if (isRowInteractiveTarget(e.target)) return;
                        if (subscriberId && (e.metaKey || e.ctrlKey)) {
                          e.preventDefault();
                          openSubscriberInNewTab(subscriberId);
                          return;
                        }
                        openRow();
                      }}
                      onAuxClick={(e) => {
                        if (e.button !== 1 || !subscriberId || isRowInteractiveTarget(e.target)) return;
                        e.preventDefault();
                        openSubscriberInNewTab(subscriberId);
                      }}
                      onContextMenu={(e) => {
                        if (!e.ctrlKey || !subscriberId || isRowInteractiveTarget(e.target)) return;
                        e.preventDefault();
                        openSubscriberInNewTab(subscriberId);
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        e.preventDefault();
                        if (subscriberId && (e.metaKey || e.ctrlKey)) {
                          openSubscriberInNewTab(subscriberId);
                          return;
                        }
                        openRow();
                      }}
                      className="cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-900/30"
                    >
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(id)}
                          onChange={() => toggleRowSelected(id)}
                          aria-label={`Select ${bestName(r) || email}`}
                          className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                      </td>
                      {visibleColumns.map((column) => (
                        <td
                          key={column.key}
                          className={`px-4 py-3 text-sm text-gray-600 dark:text-gray-300 ${
                            listColumnShowsImage(column) ? 'w-16' : ''
                          } ${
                            column.kind === 'builtin' && column.id === 'email'
                              ? 'text-gray-900 dark:text-gray-100 max-w-[min(28rem,55vw)]'
                              : column.kind === 'builtin' && column.id === 'name'
                                ? 'text-gray-700 dark:text-gray-200'
                                : ''
                          }`}
                        >
                          {column.kind === 'builtin' && column.id === 'actions' ? (
                            needsNewsletterInvite(r) ? (
                              <button
                                type="button"
                                onClick={(e) => openInviteForRow(r, e)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-amber-100 text-amber-900 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-200"
                              >
                                <Mail className="w-3 h-3" />
                                Invite
                              </button>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500">—</span>
                            )
                          ) : (
                            <>
                              {renderListFieldCell(r, column, channelCatalog)}
                              {aiFilteredIds && aiSimilarity[id] != null ? (
                                <span className="ml-2 text-[10px] font-medium text-violet-600 dark:text-violet-400">
                                  {aiSimilarity[id]}%
                                </span>
                              ) : null}
                            </>
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {sortedFiltered.length === 0 ? (
              <p className="p-8 text-center text-gray-500 dark:text-gray-400">
                {enforcedColumnCount > 0 && rows.length > 0
                  ? `No subscribers match enforced columns: ${enforcedColumns.map((c) => c.label).join(', ')}.`
                  : 'No subscribers found.'}
              </p>
            ) : null}
          </div>
        )}
      </div>

      <SubscriberModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSubscriberDraft(null);
        }}
        subscriber={current}
        initialDraft={subscriberDraft}
        onSuccess={fetchSubscribers}
      />

      <CalendlyMeetingsModal
        isOpen={calendlyModalOpen}
        onClose={() => setCalendlyModalOpen(false)}
        existingEmails={existingSubscriberEmails}
        onImport={handleCalendlyImport}
      />

      {inviteTarget ? (
        <SendSubscriberInviteModal
          isOpen={inviteModalOpen}
          onClose={() => {
            setInviteModalOpen(false);
            setInviteTarget(null);
          }}
          onSent={() => void fetchSubscribers()}
          toEmail={inviteTarget.email}
          subscriberId={inviteTarget.subscriberId}
          recipientName={inviteTarget.name}
        />
      ) : null}

      <SubscriberSelectionInsightsModal
        isOpen={insightsModalOpen}
        onClose={() => setInsightsModalOpen(false)}
        selectedRows={selectedRows}
        channelCatalog={channelCatalog}
      />

      <SubscriberBulkEditModal
        isOpen={bulkEditModalOpen}
        onClose={() => setBulkEditModalOpen(false)}
        selectedRows={selectedRows}
        channelCatalog={channelCatalog}
        knownSources={availableSources}
        onSuccess={() => {
          setSelectedIds(new Set());
          void fetchSubscribers();
        }}
      />
    </div>
  );
}

